import { useEffect, useMemo, useState } from "react";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import AppLoader from "components/AppLoader";

import fetchWithAuth from "layouts/tables/data/api";
import appSettings from "appsettings.json";
import { formatDateTimeForUser } from "utils/dateTime";

function PendingOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const normalizeArray = (value) => {
    if (Array.isArray(value)) return value;
    if (value && Array.isArray(value.$values)) return value.$values;
    return [];
  };

  const firstDefined = (...values) => values.find((value) => value !== undefined && value !== null);

  const readStatus = (item) =>
    String(
      firstDefined(
        item?.status,
        item?.Status,
        item?.orderStatus,
        item?.OrderStatus,
        item?.state,
        item?.State
      ) ?? ""
    )
      .trim()
      .toLowerCase();

  const getStatus = (item) =>
    String(
      firstDefined(
        item?.status,
        item?.Status,
        item?.orderStatus,
        item?.OrderStatus,
        item?.state,
        item?.State
      ) ?? "unknown"
    ).trim();

  const getStatusColor = (item) => {
    const status = readStatus(item);
    if (
      status.includes("pending") ||
      status.includes("awaiting") ||
      status.includes("processing")
    ) {
      return "warning";
    }
    if (status.includes("placed") || status.includes("completed") || status.includes("delivered")) {
      return "success";
    }
    if (status.includes("cancel")) {
      return "error";
    }
    return "secondary";
  };

  const isPaymentCompletedStatus = (item) => {
    const status = readStatus(item)
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
    return (
      status.includes("payment completed") ||
      (status.includes("payment") && status.includes("completed"))
    );
  };

  const getOrderId = (item) =>
    String(
      firstDefined(
        item?.orderId,
        item?.OrderId,
        item?.id,
        item?.Id,
        item?.referenceNo,
        item?.ReferenceNo
      ) ?? "-"
    );

  const getCustomer = (item) =>
    String(
      firstDefined(
        item?.customerName,
        item?.CustomerName,
        item?.customer,
        item?.Customer,
        item?.name,
        item?.Name
      ) ?? "-"
    );

  const formatAmount = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return "-";
    return amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getItemTotal = (item) =>
    formatAmount(firstDefined(item?.itemTotal, item?.ItemTotal, item?.amount, item?.Amount, 0));

  const getDeliveryFee = (item) =>
    formatAmount(firstDefined(item?.deliveryFee, item?.DeliveryFee, 0));

  const getGrandTotal = (item) =>
    formatAmount(
      firstDefined(
        item?.grandTotal,
        item?.GrandTotal,
        item?.totalAmount,
        item?.TotalAmount,
        item?.amount,
        item?.Amount,
        0
      )
    );

  const getCreatedDate = (item) => {
    const value = firstDefined(
      item?.orderDate,
      item?.OrderDate,
      item?.createdDate,
      item?.CreatedDate,
      item?.createdOn,
      item?.CreatedOn
    );

    return formatDateTimeForUser(value);
  };

  const fetchPendingOrders = async () => {
    setLoading(true);
    setError(null);

    try {
      const endpoint = appSettings?.endpoints?.getPendingOrders;
      if (!endpoint) {
        throw new Error("Pending orders endpoint is not configured.");
      }

      const url = `${appSettings.baseUrl}${endpoint}?_ts=${Date.now()}`;
      const response = await fetchWithAuth(url, { method: "GET", cache: "no-store" });

      if (!response.ok) {
        throw new Error("Failed to load pending orders.");
      }

      const data = await response.json();
      const allOrders = normalizeArray(
        firstDefined(data?.orders, data?.Orders, data?.pendingOrders, data?.PendingOrders, data)
      );
      setOrders(allOrders);
    } catch (e) {
      setError(e.message || "Failed to load pending orders.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingOrders();
  }, []);

  const tableData = useMemo(() => {
    const columns = [
      {
        Header: "order #",
        accessor: "orderIdText",
        align: "left",
        Cell: ({ row }) => row.original.orderId,
      },
      {
        Header: "customer",
        accessor: "customerText",
        align: "left",
        Cell: ({ row }) => row.original.customer,
      },
      {
        Header: "item total",
        accessor: "itemTotalValue",
        align: "right",
        Cell: ({ row }) => row.original.itemTotal,
      },
      {
        Header: "delivery fee",
        accessor: "deliveryFeeValue",
        align: "right",
        Cell: ({ row }) => row.original.deliveryFee,
      },
      {
        Header: "grand total",
        accessor: "grandTotalValue",
        align: "right",
        Cell: ({ row }) => row.original.grandTotal,
      },
      {
        Header: "status",
        accessor: "statusText",
        align: "center",
        Cell: ({ row }) => row.original.status,
      },
      {
        Header: "created",
        accessor: "createdText",
        align: "left",
        Cell: ({ row }) => row.original.created,
      },
    ];

    const rows = orders.map((item, index) => {
      const orderIdText = getOrderId(item) || `Order-${index + 1}`;
      const customerText = getCustomer(item);
      const itemTotalValue = getItemTotal(item);
      const deliveryFeeValue = getDeliveryFee(item);
      const grandTotalValue = getGrandTotal(item);
      const statusText = getStatus(item);
      const createdText = getCreatedDate(item);

      return {
        orderIdText,
        orderId: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {orderIdText}
          </MDTypography>
        ),
        customerText,
        customer: (
          <MDTypography variant="caption" color="text">
            {customerText}
          </MDTypography>
        ),
        itemTotalValue,
        itemTotal: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {itemTotalValue}
          </MDTypography>
        ),
        deliveryFeeValue,
        deliveryFee: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {deliveryFeeValue}
          </MDTypography>
        ),
        grandTotalValue,
        grandTotal: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {grandTotalValue}
          </MDTypography>
        ),
        statusText,
        status: (
          <MDBadge
            badgeContent={statusText}
            color={getStatusColor(item)}
            variant="gradient"
            size="sm"
            sx={
              isPaymentCompletedStatus(item)
                ? {
                    background: "linear-gradient(195deg, #4caf50, #2e7d32) !important",
                    color: "#ffffff !important",
                  }
                : undefined
            }
          />
        ),
        createdText,
        created: (
          <MDTypography variant="caption" color="text">
            {createdText}
          </MDTypography>
        ),
      };
    });

    return { columns, rows };
  }, [orders]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="primary"
                borderRadius="lg"
                coloredShadow="primary"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  All Orders
                </MDTypography>
                <MDButton variant="gradient" color="primary" onClick={fetchPendingOrders}>
                  <Icon sx={{ fontWeight: "bold" }}>refresh</Icon>
                  &nbsp;refresh
                </MDButton>
              </MDBox>

              <MDBox pt={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" alignItems="center" py={10}>
                    <AppLoader size={42} />
                  </MDBox>
                ) : (
                  <>
                    {error && (
                      <MDBox
                        mx={2}
                        mb={2}
                        py={1}
                        px={2}
                        variant="gradient"
                        sx={{ backgroundColor: "lightcoral" }}
                        borderRadius="lg"
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <MDTypography variant="button" color="white" fontWeight="medium">
                          {error}
                        </MDTypography>
                        <MDTypography
                          color="white"
                          sx={{ cursor: "pointer", ml: 2, lineHeight: 0 }}
                          onClick={() => setError(null)}
                        >
                          x
                        </MDTypography>
                      </MDBox>
                    )}

                    <DataTable
                      table={tableData}
                      isSorted
                      canSearch
                      entriesPerPage={{ defaultValue: 10, entries: [5, 10, 25, 50] }}
                      showTotalEntries
                      noEndBorder
                    />
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default PendingOrders;
